import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { SEVERITY, REVIEW, STATUS, num, moneyParts } from "../lib/format";
import { CountUp } from "./CountUp";

export function SeverityBadge({ severity }) {
  const s = SEVERITY[severity] || SEVERITY[1];
  return (
    <span
      data-testid="severity-badge"
      className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export function ReviewBadge({ status }) {
  const cls = REVIEW[status] || REVIEW.NEW;
  return (
    <span
      data-testid="review-badge"
      className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${cls}`}
    >
      {(status || "NEW").replace("_", " ")}
    </span>
  );
}

export function StatusPill({ status }) {
  const cls = STATUS[status] || "text-muted-foreground";
  return (
    <span className={`font-mono text-xs font-semibold ${cls}`}>{(status || "").replace("_", " ")}</span>
  );
}

export function Variance({ delta, className = "" }) {
  if (delta == null) return <span className="text-muted-foreground font-mono">—</span>;
  const up = delta > 0;
  const flat = delta === 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const color = flat ? "text-muted-foreground" : up ? "text-pos" : "text-neg";
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-semibold ${color} ${className}`}>
      <Icon size={14} strokeWidth={2.5} />
      {num(Math.abs(delta), 2)}
    </span>
  );
}

export function StatCard({ label, value, sub, accent = "", bar = "bg-foreground/25", count, decimals = 0, testid }) {
  return (
    <div
      data-testid={testid}
      className="group relative overflow-hidden rounded-md border border-border bg-card p-5 transition-all duration-200 hover:border-foreground/30 hover:-translate-y-0.5"
    >
      <div className={`absolute inset-x-0 top-0 h-[3px] ${bar}`} />
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-2 font-mono text-3xl font-bold ${accent}`}>
        {count != null ? <CountUp value={count} decimals={decimals} /> : value}
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Money({ value, ccy }) {
  const { main, cents } = moneyParts(value, ccy);
  return (
    <span className="font-mono tabular">
      {main}
      {cents && <span className="text-muted-foreground">{cents}</span>}
    </span>
  );
}

export function VarianceBar({ value, max, className = "" }) {
  if (value == null || !max) return null;
  const w = Math.min(100, (Math.abs(value) / max) * 100);
  const up = value > 0;
  return (
    <div className={`h-1.5 w-full rounded-full bg-muted overflow-hidden ${className}`}>
      <div
        className={`h-full rounded-full transition-all ${up ? "bg-pos" : "bg-neg"}`}
        style={{ width: `${w}%` }}
      />
    </div>
  );
}
